import { useCallback, useMemo, useState } from 'react';

interface Props<T> {
  items: T[];
  initialSize: number;
  stepSize?: number;
}

const usePagination = <T>({ items, initialSize, stepSize }: Props<T>) => {
  const [step, setStep] = useState(1);

  const handleNext = useCallback(() => {
    setStep((currentStep) => currentStep + 1);
  }, [setStep]);

  const slicedItems = useMemo(() => {
    if (!stepSize && step > 1) {
      return items;
    }
    const totalItems = step === 1 ? initialSize : initialSize + (step - 1) * (stepSize ?? 0);
    return items.slice(0, totalItems);
  }, [items, initialSize, step, stepSize]);

  const reset = useCallback(() => setStep(1), []);

  const hasNextItems = useMemo(() => items?.length > slicedItems?.length, [items, slicedItems]);

  return {
    slicedItems,
    hasNextItems,
    handleNext,
    reset,
  };
};

export default usePagination;
